import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, IconButton, Chip, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AnnouncementMedia from "./AnnouncementMedia";

const AnnouncementModalPreview = ({ open, onClose, announcement }) => {
  const [selectedLang, setSelectedLang] = useState("en");

  if (!announcement) return null;

  const contentMap = announcement.content || {};
  const availableLangs = Object.keys(contentMap);
  const activeLang = contentMap[selectedLang] ? selectedLang : availableLangs[0] || "en";
  const currentContent = contentMap[activeLang] || {};

  const { title = "No Title", message = "No Message", buttonLabel = "Learn More", mediaType = "NONE", mediaUrl = "" } = currentContent;

  const { buttonUrl, buttonTargetType = "INTERNAL", expiresAt, showButton = true } = announcement;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* Header with Language Selector & Close */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 1.5,
          bgcolor: "grey.50",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            LANG:
          </Typography>
          {availableLangs.map((lang) => (
            <Chip
              key={lang}
              label={lang.toUpperCase()}
              size="small"
              clickable
              color={activeLang === lang ? "primary" : "default"}
              variant={activeLang === lang ? "filled" : "outlined"}
              onClick={() => setSelectedLang(lang)}
            />
          ))}
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Media Banner */}
      <AnnouncementMedia mediaType={mediaType} mediaUrl={mediaUrl} title={title} />

      <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: "text.primary" }}>
          {title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            whiteSpace: "pre-wrap",
            lineHeight: 1.6,
            mt: 1,
          }}
        >
          {message}
        </Typography>

        {expiresAt && (
          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.disabled" }}>
            Valid until: {new Date(expiresAt).toLocaleDateString()}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: "space-between" }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 600 }}>
          Dismiss
        </Button>

        {showButton && buttonLabel && buttonUrl && (
          <Button
            variant="contained"
            color="primary"
            endIcon={buttonTargetType === "EXTERNAL" ? <OpenInNewIcon /> : <ArrowForwardIcon />}
            href={buttonUrl}
            target={buttonTargetType === "EXTERNAL" ? "_blank" : "_self"}
            rel={buttonTargetType === "EXTERNAL" ? "noopener noreferrer" : undefined}
            onClick={onClose}
            sx={{ px: 3, py: 1, borderRadius: 2, fontWeight: 600 }}
          >
            {buttonLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementModalPreview;
